import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Form as AntForm,
  Input,
  InputNumber,
  Modal,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import {
  PlusOutlined,
  ReloadOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { authFetch } from '../../utils/api';
import { BrandScopeNotice } from '../../components/BrandScopeNotice';
import {
  canMutateSharedInfra,
  SHARED_INFRA_READONLY_MESSAGE,
} from '../../utils/permissions';
import {
  DangerousActionModal,
  FormActions,
  FormField,
} from '../../shared/form';
import {
  ApiDomainUpsertSchema,
  type ApiDomainUpsertValues,
} from '../../schemas/apiDomain';

interface ApiDomainRecord {
  id: number;
  origin: string;
  isActive: boolean;
  priority: number;
  note: string | null;
  updatedAt?: string;
}

const emptyValues: ApiDomainUpsertValues = {
  origin: '',
  isActive: true,
  priority: 100,
  note: '',
};

function formatDate(value?: string | null) {
  return value ? new Date(value).toLocaleString() : '-';
}

async function requestJson(url: string, options?: RequestInit) {
  const response = await authFetch(url, options);
  return response.json();
}

export const ApiDomainPoolPage: React.FC = () => {
  const sharedInfraReadOnly = !canMutateSharedInfra();
  const [domains, setDomains] = useState<ApiDomainRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<ApiDomainRecord | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ApiDomainRecord | null>(
    null
  );
  const [updateConfirmOpen, setUpdateConfirmOpen] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const form = useForm<ApiDomainUpsertValues>({
    resolver: zodResolver(ApiDomainUpsertSchema),
    mode: 'onBlur',
    defaultValues: emptyValues,
  });

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const result = await requestJson('/api/admin/api-domains');
      if (result.code === 0 && Array.isArray(result.data)) {
        setDomains(result.data);
      } else {
        message.error(result.msg || 'API 域名資料載入失敗');
      }
    } catch {
      message.error('API 域名資料載入失敗');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const openCreate = () => {
    if (sharedInfraReadOnly) {
      message.warning(SHARED_INFRA_READONLY_MESSAGE);
      return;
    }
    setEditing(null);
    form.reset(emptyValues);
    setModalOpen(true);
  };

  const openEdit = (record: ApiDomainRecord) => {
    if (sharedInfraReadOnly) {
      message.warning(SHARED_INFRA_READONLY_MESSAGE);
      return;
    }
    setEditing(record);
    form.reset({
      origin: record.origin,
      isActive: record.isActive,
      priority: record.priority,
      note: record.note ?? '',
    });
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    form.reset(emptyValues);
  };

  const save = async (values: ApiDomainUpsertValues) => {
    const result = await requestJson(
      editing
        ? `/api/admin/api-domains/${editing.id}`
        : '/api/admin/api-domains',
      {
        method: editing ? 'PUT' : 'POST',
        body: JSON.stringify({ ...values, note: values.note || undefined }),
      }
    );
    if (result.code !== 0) {
      if (result.msg?.includes('重複')) {
        form.setError('origin', { message: result.msg });
      } else {
        message.error(result.msg || '儲存失敗');
      }
      return;
    }
    message.success(editing ? 'API URL 已更新' : 'API URL 已新增');
    closeModal();
    void refresh();
  };

  const deleteDomain = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const result = await requestJson(
        `/api/admin/api-domains/${deleteTarget.id}`,
        { method: 'DELETE' }
      );
      if (result.code === 0) {
        message.success('API URL 已刪除');
        setDeleteTarget(null);
        void refresh();
      } else {
        message.error(result.msg || '刪除失敗');
      }
    } finally {
      setDeleting(false);
    }
  };

  const requestUpdate = async () => {
    setUpdating(true);
    try {
      const result = await requestJson('/api/admin/api-domains/update', {
        method: 'POST',
      });
      if (result.code === 0) {
        message.success('更新請求已送交 API Gateway');
      } else {
        message.error(result.msg || '更新請求失敗');
      }
      setUpdateConfirmOpen(false);
    } finally {
      setUpdating(false);
    }
  };

  const activeCount = domains.filter(domain => domain.isActive).length;

  return (
    <div className="page-enter">
      <Card
        title="API 動態域名"
        extra={
          <Space wrap>
            <Button
              icon={<ReloadOutlined />}
              loading={loading}
              onClick={() => void refresh()}
            >
              重新整理
            </Button>
            <Button
              icon={<UploadOutlined />}
              disabled={sharedInfraReadOnly || activeCount === 0}
              onClick={() => setUpdateConfirmOpen(true)}
            >
              更新動態域名
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              disabled={sharedInfraReadOnly}
              onClick={openCreate}
            >
              新增 API URL
            </Button>
          </Space>
        }
      >
        <BrandScopeNotice kind="shared-infra" style={{ marginBottom: 16 }} />
        <Alert
          showIcon
          type={activeCount > 10 ? 'error' : 'info'}
          style={{ marginBottom: 16 }}
          message={`目前啟用 ${activeCount} / 10 組 API URL`}
          description="所有 VPN 品牌共用此域名池。更新動態域名會送交 API Gateway；Lambda 會讀取目前啟用的 URL，負責加密與發布。"
        />
        <Table<ApiDomainRecord>
          rowKey="id"
          loading={loading}
          dataSource={domains}
          pagination={false}
          scroll={{ x: 960 }}
          columns={[
            {
              title: '優先級',
              dataIndex: 'priority',
              width: 100,
              sorter: (a, b) => a.priority - b.priority,
            },
            {
              title: 'API URL',
              dataIndex: 'origin',
              width: 360,
              ellipsis: true,
            },
            {
              title: '狀態',
              dataIndex: 'isActive',
              width: 100,
              render: (isActive: boolean) => (
                <Tag color={isActive ? 'success' : 'default'}>
                  {isActive ? '啟用' : '停用'}
                </Tag>
              ),
            },
            {
              title: '備註',
              dataIndex: 'note',
              ellipsis: true,
              render: (note: string | null) => note || '-',
            },
            {
              title: '更新時間',
              dataIndex: 'updatedAt',
              width: 180,
              render: formatDate,
            },
            {
              title: '操作',
              key: 'actions',
              width: 160,
              fixed: 'right',
              render: (_, record) => (
                <Space>
                  <Button
                    size="small"
                    disabled={sharedInfraReadOnly}
                    onClick={() => openEdit(record)}
                  >
                    編輯
                  </Button>
                  <Button
                    size="small"
                    danger
                    disabled={sharedInfraReadOnly}
                    onClick={() => setDeleteTarget(record)}
                  >
                    刪除
                  </Button>
                </Space>
              ),
            },
          ]}
        />
      </Card>

      <Modal
        title={editing ? `編輯 API URL：${editing.origin}` : '新增 API URL'}
        open={modalOpen}
        onCancel={closeModal}
        footer={null}
        destroyOnHidden
      >
        <FormProvider {...form}>
          <AntForm layout="vertical" onFinish={form.handleSubmit(save)}>
            <FormField<ApiDomainUpsertValues>
              name="origin"
              label="API URL"
              required
              help="僅接受 HTTPS origin，例如 https://api-01.example.com"
              control={<Input placeholder="https://api-01.example.com" />}
            />
            <FormField<ApiDomainUpsertValues>
              name="isActive"
              label="啟用"
              valuePropName="checked"
              control={
                <Switch checkedChildren="啟用" unCheckedChildren="停用" />
              }
            />
            <FormField<ApiDomainUpsertValues>
              name="priority"
              label="優先級"
              required
              help="數字較小的 URL 會優先提供給 Lambda。"
              control={
                <InputNumber min={0} max={10000} style={{ width: '100%' }} />
              }
            />
            <FormField<ApiDomainUpsertValues>
              name="note"
              label="備註"
              control={
                <Input.TextArea rows={3} placeholder="選填，僅供 BO 管理使用" />
              }
            />
            <FormActions
              onCancel={closeModal}
              submitting={form.formState.isSubmitting}
              dirty={form.formState.isDirty}
              submitLabel={editing ? '儲存變更' : '新增'}
            />
          </AntForm>
        </FormProvider>
      </Modal>

      <DangerousActionModal
        open={!!deleteTarget}
        title={`刪除 API URL：${deleteTarget?.origin ?? ''}`}
        warning="此操作會從共用 API URL 池移除該 URL，下一次 Lambda 更新不會再包含它。"
        confirmKeyword={deleteTarget?.origin ?? ''}
        confirming={deleting}
        onConfirm={() => void deleteDomain()}
        onCancel={() => setDeleteTarget(null)}
        confirmLabel="確認刪除"
      />

      <DangerousActionModal
        open={updateConfirmOpen}
        title="更新 API 動態域名"
        warning={`將送出目前 ${activeCount} 組啟用 URL 的更新請求至 API Gateway。Lambda 會讀取域名池、加密並發布至各固定路徑；BO 僅確認 API Gateway 是否接受請求。`}
        confirmKeyword="更新域名"
        confirmHint={
          <>
            請輸入 <Typography.Text code>更新域名</Typography.Text> 確認：
          </>
        }
        confirming={updating}
        onConfirm={() => void requestUpdate()}
        onCancel={() => setUpdateConfirmOpen(false)}
        confirmLabel="確認更新動態域名"
      />
    </div>
  );
};
